import { useEffect, type JSX } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import { AgeGateModal } from './components/AgeGateModal';
import { AuthModal } from './components/auth/AuthModal';
import { CookieBanner } from './components/CookieBanner';
import { MaintenanceModal } from './components/MaintenanceModal';
import { NotificationCenter } from './components/notifications/NotificationCenter';
import { OfflineBanner } from './components/OfflineBanner';
import { registerServiceWorker } from './lib/push/pushManager';
import { useMe } from './queries/useMe';
import { useAuthStore } from './store/useAuthStore';
import { useEntryStore } from './store/useEntryStore';
import { useFingerprintStore } from './store/useFingerprintStore';
import { useModalStack } from './store/useModalStack';

const PUBLIC_LEGAL_PATHS = new Set(['/legal', '/access-denied', '/age-restricted', '/tos', '/privacy', '/404']);

export function App(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Bootstraps auth state on first mount; subsequent updates sync via Zustand.
  useMe();

  const entryStatus = useEntryStore((s) => s.status);
  const entryData = useEntryStore((s) => s.data);
  const refetchEntry = useEntryStore((s) => s.refetch);

  // Bootstraps entry orchestrator state on first mount.
  useEffect(() => {
    if (entryStatus === 'idle') {
      void refetchEntry();
    }
  }, [entryStatus, refetchEntry]);

  // Bootstraps device fingerprint collection on first mount (B06a CF-2000).
  // Runs once; subsequent auth flows (signup, Guest, OAuth) attach the hash.
  const fingerprintStatus = useFingerprintStore((s) => s.status);
  const fingerprintHex = useFingerprintStore((s) => s.hash_hex);
  const collectFingerprint = useFingerprintStore((s) => s.collect);
  useEffect(() => {
    if (fingerprintStatus === 'idle') {
      void collectFingerprint();
    }
  }, [fingerprintStatus, collectFingerprint]);

  // B06a: after auth, redirect to /onboarding when onboarding not complete.
  // AgeVerificationFlow page then sorts: if `next_step==='app_main'` (cache hit),
  // it redirects to /app immediately; otherwise renders Step 1 + age verification.
  const authStateForOnboarding = useAuthStore((s) => s.authState);
  useEffect(() => {
    if (authStateForOnboarding !== 'authenticated') return;
    // Avoid stomping users browsing legal pages.
    if (PUBLIC_LEGAL_PATHS.has(location.pathname)) return;
    // Already on the flow / on app — let pages handle.
    if (location.pathname === '/onboarding' || location.pathname === '/app') return;
    // From landing or any other route, take them through onboarding.
    if (location.pathname === '/') {
      navigate('/onboarding', { replace: true });
    }
  }, [authStateForOnboarding, location.pathname, navigate]);
  // Reference fingerprintHex so the linter doesn't drop the subscription —
  // useful for downstream effects (debug, fingerprint-aware redirects future).
  void fingerprintHex;

  // Redirect to /access-denied when blacklist BLOCKED arrives (except for legal pages).
  useEffect(() => {
    if (!entryData) return;
    if (entryData.blacklist.status !== 'BLOCKED') return;
    if (PUBLIC_LEGAL_PATHS.has(location.pathname)) return;
    const params = new URLSearchParams();
    if (entryData.blacklist.reason) params.set('reason', entryData.blacklist.reason);
    if (entryData.blacklist.code) params.set('code', entryData.blacklist.code);
    navigate(`/access-denied?${params.toString()}`, { replace: true });
  }, [entryData, location.pathname, navigate]);

  // Auto-open AuthModal after Age Gate submit, when:
  //  - entry orchestrator says auth required + age gate done + blacklist OK
  //  - user is NOT authenticated
  //  - we're on the landing path (not on legal/match/etc.)
  const openAuthModal = useAuthStore((s) => s.openAuthModal);
  const authState = useAuthStore((s) => s.authState);
  const authModalOpen = useAuthStore((s) => s.authModalOpen);
  useEffect(() => {
    if (!entryData) return;
    if (entryData.blacklist.status === 'BLOCKED') return;
    if (entryData.age_gate.required) return;
    if (!entryData.auth_required) return;
    if (authState !== 'unauthenticated') return;
    if (authModalOpen) return;
    if (location.pathname !== '/') return;
    openAuthModal('login');
  }, [entryData, authState, authModalOpen, location.pathname, openAuthModal]);

  // Global Back-button handler: close the top-most modal instead of navigating.
  useEffect(() => {
    const onPop = (): void => {
      useModalStack.getState().closeTop();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // B15: register the push service worker once (idempotent).
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  return (
    <>
      {authState === 'authenticated' && (
        <Box sx={{ position: 'fixed', top: 8, right: 8, zIndex: (t) => t.zIndex.appBar + 1 }}>
          <NotificationCenter />
        </Box>
      )}
      <Outlet />
      <CookieBanner />
      <AgeGateModal />
      <AuthModal />
      <MaintenanceModal />
      <OfflineBanner />
    </>
  );
}
